import React,{useEffect,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {Camera,Loader2,X} from 'lucide-react';
import {extractNfeAccessKey} from './nfeKeyCapture.js';

// Dedicated NF-e 55 scanner. Retail GTIN validation does not apply to a 44-digit DANFE key.
// Reuses the installed ZXing browser dependency and the existing scanner lifecycle pattern.
export default function NfeKeyScanner({onDetected,onClose}){
  const [status,setStatus]=useState('opening');
  const [error,setError]=useState('');
  const videoRef=useRef(null);
  const doneRef=useRef(onDetected);
  const closeRef=useRef(onClose);
  doneRef.current=onDetected;
  closeRef.current=onClose;
  useEffect(()=>{
    let disposed=false,accepted=false,scannerControls,scanTimer=null,fallbackStarted=false;
    const video=videoRef.current;
    const stop=()=>{
      if(scanTimer){clearTimeout(scanTimer);scanTimer=null}
      scannerControls?.stop?.();
      scannerControls=null;
      const stream=video?.srcObject;
      stream?.getTracks?.().forEach(t=>t.stop());
      if(video) video.srcObject=null;
    };
    const accept=raw=>{
      if(disposed||accepted)return;
      try {
        const key=extractNfeAccessKey(raw);
        accepted=true;
        stop();
        try{navigator.vibrate?.(65)}catch{}
        doneRef.current?.(key);
        closeRef.current?.();
      }catch(e){
        if(!disposed) setError(e.message);
      }
    };
    const keyHandler=e=>{if(e.key==='Escape')closeRef.current?.()};
    document.addEventListener('keydown',keyHandler);

    async function startZXing(){
      if(disposed||accepted||fallbackStarted)return;
      fallbackStarted=true;
      try {
        const {BrowserMultiFormatReader}=await import('@zxing/browser');
        if(disposed||accepted)return;
        const reader=new BrowserMultiFormatReader(undefined,{
          delayBetweenScanAttempts:130,
          delayBetweenScanSuccess:450,
        });
        const controls=await reader.decodeFromVideoElement(video,result=>{
          if(result) accept(result.getText?.());
        });
        if(disposed||accepted) controls?.stop?.();
        else scannerControls=controls;
      }catch(e){
        if(!disposed&&!accepted){
          setStatus('error');
          setError('Não foi possível iniciar o leitor. Use a opção Fotografar ou digite a chave.');
        }
      }
    }
    async function start(){
      if(!window.isSecureContext||!navigator.mediaDevices?.getUserMedia){
        setStatus('error');
        setError('Câmera indisponível. Fotografe a nota ou digite a chave.');
        return;
      }
      try {
        const stream=await navigator.mediaDevices.getUserMedia({
          audio:false,video:{facingMode:{ideal:'environment'},
            width:{ideal:1920},height:{ideal:1080}}
        });
        if(disposed){stream.getTracks().forEach(t=>t.stop());return}
        video.srcObject=stream;
        await video.play();
        if(disposed||accepted)return;
        setStatus('ready');
        const Native=window.BarcodeDetector;
        if(typeof Native!=='function'){await startZXing();return}
        let detector;
        try {
          const supported=await Native.getSupportedFormats();
          const formats=['code_128','qr_code','itf'].filter(x=>supported.includes(x));
          if(!formats.length){await startZXing();return}
          detector=new Native({formats});
        }catch{await startZXing();return}
        const startAt=Date.now();
        async function readNative(){
          if(disposed||accepted||fallbackStarted)return;
          if(Date.now()-startAt>2600){await startZXing();return}
          try {
            if(video.readyState>=2) {
              const results=await detector.detect(video);
              for(const result of results||[]){
                if(disposed||accepted)return;
                accept(result.rawValue);
              }
            }
          }catch {await startZXing();return}
          if(!disposed&&!accepted&&!fallbackStarted)scanTimer=setTimeout(readNative,135);
        }
        await readNative();
      }catch(e){
        if(disposed||accepted)return;
        setStatus('error');
        setError(e?.name==='NotAllowedError'?
          'Permissão de câmera bloqueada. Autorize o acesso nas configurações do Android.':
          'Não foi possível abrir a câmera. Use a foto do DANFE ou digite a chave.');
      }
    }
    start();
    return ()=>{disposed=true;document.removeEventListener('keydown',keyHandler);stop()};
  },[]);
  return createPortal(
    <div className="posLiveScanner nfeKeyScanner" role="dialog" aria-modal="true"
         aria-label="Ler código da nota fiscal com câmera">
      <div className="posLiveScannerTop">
        <button type="button" aria-label="Fechar câmera" onClick={()=>closeRef.current?.()}><X size={25}/></button>
        <strong>Escanear DANFE</strong>
        <span aria-hidden="true"/>
      </div>
      <div className="posLiveScannerView">
        <video ref={videoRef} autoPlay playsInline muted aria-label="Câmera para código de barras ou QR Code da NF-e"/>
        <div className="posLiveScannerTarget" aria-hidden="true"><span/><span/><span/><span/></div>
        {status==='opening'&&<div className="posLiveScannerLoading" role="status">
          <Loader2 size={22} className="spin"/> Abrindo câmera…
        </div>}
      </div>
      <div className="posLiveScannerBottom">
        <Camera size={22} aria-hidden="true"/>
        <p>Aponte para o código de barras de 44 dígitos no DANFE da NF-e modelo 55.
          O QR Code também será analisado quando contiver a chave. Nenhuma mercadoria será lançada.</p>
        {error&&<p className="posLiveScannerError" role="alert">{error}</p>}
        <button type="button" onClick={()=>closeRef.current?.()}>Fechar e digitar</button>
      </div>
    </div>,document.body
  );
}
